import { BookOpen, ShieldAlert, FileCheck2, AlertTriangle, ScrollText, ExternalLink, Library } from "lucide-react";
import { cn } from "@/lib/utils";

// Match the chip styles used in ClassifierOutputCard so the same status reads
// the same wherever it appears.
const STATUS_STYLES = {
  verified: { chip: "bg-success-soft text-success border-success/40", icon: FileCheck2 },
  requires_source_verification: { chip: "bg-warning-soft text-warning border-warning/40", icon: AlertTriangle },
  internal_only: { chip: "bg-info-soft text-info border-info/40", icon: ScrollText },
  do_not_publish: { chip: "bg-destructive text-destructive-foreground border-destructive", icon: ShieldAlert }
};

const SOURCE_STATUS_STYLES = {
  CURRENT: { chip: "bg-success-soft text-success border-success/40" },
  REQUIRES_REVIEW: { chip: "bg-warning-soft text-warning border-warning/40" },
  INTERNAL_ONLY: { chip: "bg-info-soft text-info border-info/40" },
  DO_NOT_PUBLISH: { chip: "bg-destructive-soft text-destructive border-destructive/30" }
};

function SourceRow({ source }) {
  const statusStyle = SOURCE_STATUS_STYLES[source.status] || SOURCE_STATUS_STYLES.REQUIRES_REVIEW;
  return (
    <li
      data-testid={`source-entry-${source.sourceId}`}
      className="grid grid-cols-1 gap-2 rounded-sm border border-border bg-card/70 p-4 md:grid-cols-12 md:items-start md:gap-4"
    >
      <div className="md:col-span-7">
        <p className="font-display text-[14px] font-medium leading-snug text-foreground">
          {source.title}
        </p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{source.notes}</p>
        {source.url && (
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-accent hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            {source.url}
          </a>
        )}
      </div>
      <div className="md:col-span-3">
        <p className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          Authority
        </p>
        <p className="text-xs text-foreground">{source.authorityLevel}</p>
        <p className="mt-2 font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          Last reviewed
        </p>
        <p className="text-xs text-foreground">{source.lastReviewedDate || "\u2014"}</p>
      </div>
      <div className="md:col-span-2 md:text-right">
        <span
          className={cn(
            "inline-flex rounded-sm border px-2 py-0.5 font-mono-label text-[10px] uppercase tracking-[0.18em]",
            statusStyle.chip
          )}
        >
          {source.status.replace(/_/g, " ")}
        </span>
      </div>
    </li>
  );
}

function SourceList({ title, sources, eyebrow, emptyLabel, testid }) {
  return (
    <div data-testid={testid} className="classifier-panel rounded-sm border border-border bg-card/40 p-5">
      <div className="mb-3 flex items-center gap-2">
        <Library className="h-4 w-4 text-foreground" />
        <p className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          {eyebrow}
        </p>
        <span className="ml-auto font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {sources.length} source{sources.length === 1 ? "" : "s"}
        </span>
      </div>
      <h4 className="mb-3 font-display text-[15px] font-medium text-foreground">{title}</h4>
      {sources.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyLabel}</p>
      ) : (
        <ul className="space-y-2">
          {sources.map((s) => (
            <SourceRow key={s.sourceId} source={s} />
          ))}
        </ul>
      )}
    </div>
  );
}

export function SourceVerificationPanel({ verification }) {
  if (!verification) return null;
  const statusStyle = STATUS_STYLES[verification.status] || STATUS_STYLES.requires_source_verification;
  const StatusIcon = statusStyle.icon;
  return (
    <section
      data-testid="source-verification-panel"
      className="classifier-card paper-card overflow-hidden"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card/80 px-6 py-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-foreground" />
          <div>
            <p className="eyebrow">Source verification</p>
            <h3 className="font-display text-lg font-medium text-foreground">
              Source basis &amp; verification
            </h3>
          </div>
        </div>
        <span
          data-testid="source-verification-status-chip"
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-sm border px-2.5 py-1 font-mono-label text-[11px] uppercase tracking-[0.18em]",
            statusStyle.chip
          )}
        >
          <StatusIcon className="h-3.5 w-3.5" />
          {verification.statusLabel}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 px-6 py-6 md:grid-cols-3">
        <div className="classifier-field md:col-span-2">
          <p className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Source basis
          </p>
          <p
            data-testid="source-verification-basis"
            className="mt-1 text-[14px] leading-relaxed text-foreground"
          >
            {verification.sourceBasis}
          </p>
        </div>
        <div className="classifier-field">
          <p className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Last reviewed
          </p>
          <p
            data-testid="source-verification-last-reviewed"
            className="mt-1 font-display text-[15px] font-medium text-foreground"
          >
            {verification.lastReviewedDate || "\u2014"}
          </p>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            Most recent review date across the sources listed below. Reconfirm before relying on this report externally.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 px-6 pb-6 md:grid-cols-2">
        <SourceList
          testid="source-verification-official"
          eyebrow="Official sources relied on"
          title="NDIS Commission / NDIA"
          sources={verification.officialSources}
          emptyLabel="No official sources are mapped to this case."
        />
        <SourceList
          testid="source-verification-internal"
          eyebrow="Internal &amp; founding sources relied on"
          title="Consultancy-authored sources"
          sources={verification.internalSources}
          emptyLabel="No internal sources recorded."
        />
      </div>

      <div
        data-testid="source-verification-notes"
        className="classifier-panel border-t border-border bg-muted/40 px-6 py-5"
      >
        <p className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          Requires source verification — notes
        </p>
        <ul className="mt-2 space-y-1.5">
          {verification.requiresVerificationNotes.map((n) => (
            <li key={n} className="flex items-start gap-2 text-[13px] leading-relaxed text-foreground">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-warning" />
              {n}
            </li>
          ))}
        </ul>
      </div>

      <div
        data-testid="source-verification-boundary"
        className="classifier-boundary border-t border-border bg-muted/40 px-6 py-4 text-xs leading-relaxed text-muted-foreground"
      >
        <span className="font-mono-label text-[10px] uppercase tracking-[0.18em]">
          Boundary statement —{" "}
        </span>
        {verification.boundaryStatement}
      </div>
    </section>
  );
}
